// Review And Decide. The approver's page, opened from their own single-use email link: ?eewp=EEWP-1004&k=...&co=alltech
// Words: EEWP_WORDS.md rev 3, "How approvers decide". Mockup: MOCKUP_EEWP.html rev 2b (approved 2026-10-12).
// No sign-in. The link is the key: it names the approver, and a decision once made stays.
// One phone screen. Approve and Deny side by side, same size. Deny asks for one line.

import "./eewp-decide.css";
import { callServer } from "./firebase";
import { COVER_SCOPE_LINE, UNSURE_LINE, approverRows, clockOf, printHtmlOf, type PermitView } from "./eewp";

interface DecisionView {
  permit: PermitView;
  you: { name: string; role: string; decision: "approved" | "denied" | null; at: string | null };
  photo: string | null;
  companyName: string;
}

function esc(v: unknown): string {
  return String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
function messageOf(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

const bar = (id: string) => `<div class="ap-bar"><b>SafeTag ESP</b><span>${esc(id)}</span></div>`;

/** The decided screen: what was recorded, in whose name. */
export function decidedHtml(v: DecisionView): string {
  const p = v.permit;
  const yes = v.you.decision === "approved";
  const all = p.status === "Approved";
  let h = `${bar(p.permitId)}<div class="ap-body"><div class="ap-done ${yes ? "y" : "n"}">${yes ? "You approved" : "You denied"} ${esc(p.permitId)}.</div>`;
  h += `<p>Recorded as ${esc(v.you.name)}, ${esc(v.you.role)}${v.you.at ? ` at ${esc(clockOf(v.you.at))}` : ""}. A decision cannot be changed.</p>`;
  if (!yes) h += "<p>The QEW sees your reason. The work can still be done with the power off.</p>";
  else if (all) h += "<p>Every approver said yes. The QEW confirms each piece of PPE before the work begins.</p>";
  else if (p.status === "Waiting") {
    const got = p.approvals.filter((a) => a.decision === "approved").length;
    h += `<p>${got} of ${p.approvalsRequired} approved so far. Every approver must say yes.</p>`;
  } else h += `<p>${esc(p.permitId)} is ${esc(p.status.toLowerCase())}. ${esc(p.endReason)}</p>`;
  return `${h}<button type="button" class="ap-print" data-ap="print">Print Permit</button></div>`;
}

/** One screen: who you are deciding as, what is asked, what it risks, and two buttons. */
export function decideHtml(v: DecisionView, reason: string, busy: boolean, error: string): string {
  const p = v.permit;
  if (v.you.decision) return decidedHtml(v);
  if (p.status !== "Waiting") {
    return `${bar(p.permitId)}<div class="ap-body"><div class="ap-done n">${esc(p.permitId)} is ${esc(p.status.toLowerCase())}.</div><p>${esc(p.endReason || "Nothing to decide.")}</p><p>Nothing to decide.</p></div>`;
  }
  const rows = approverRows(p).filter((r) => r.label !== "Permit");
  const got = p.approvals.filter((a) => a.decision === "approved").length;
  let h = `${bar(p.permitId)}<div class="ap-body">`;
  h += `<div class="ap-who">You are deciding as <b>${esc(v.you.name)}, ${esc(v.you.role)}</b>.</div>`;
  h += `<h2>Live electrical work on ${esc(p.equipment || p.assetId)}</h2>`;
  h += `<p>${esc(p.requester.name)} is asking to work on it with the power on, at ${esc(p.siteName)}. If something goes wrong, it can cause severe burns or death.</p>`;
  if (v.photo) h += `<img class="ap-photo" src="${v.photo}" alt="Photo of the work area">`;
  h += `<dl>${rows.map((r) => `<dt>${esc(r.label)}</dt><dd>${esc(r.value)}</dd>`).join("")}</dl>`;
  if (p.scope === "coverRemoval" && !rows.some((r) => r.value === COVER_SCOPE_LINE)) h += `<p class="sm">${esc(COVER_SCOPE_LINE)}</p>`;
  h += `<details><summary>Full permit</summary><dl>${p.jspHalf.map((r) => `<dt>${esc(r.label)}</dt><dd>${esc(r.value)}</dd>`).join("")}
    <dt>If something goes wrong</dt><dd>${esc(p.emergencyPlan)}</dd><dt>Part done dead</dt><dd>${esc(p.partDead === "yes" ? `Yes. ${p.partDeadWhat}` : "No")}</dd></dl></details>`;
  h += `<p class="sm">Every approver must say yes. ${got} of ${p.approvalsRequired} so far.</p>`;
  h += `<div class="ap-acts"><button type="button" class="ap-no" data-ap="deny"${busy ? " disabled" : ""}>Deny</button><button type="button" class="ap-yes" data-ap="approve"${busy ? " disabled" : ""}>Approve</button></div>`;
  h += `<textarea id="ap-reason" class="ap-ta" maxlength="300" placeholder="Reason, required for Deny" aria-label="Reason for Deny">${esc(reason)}</textarea>`;
  if (error) h += `<div class="ap-err">${esc(error)}</div>`;
  if (busy) h += '<p class="sm">Recording…</p>';
  h += `<p class="ap-unsure">${esc(UNSURE_LINE)}</p>`;
  return `${h}<button type="button" class="ap-print" data-ap="print">Print Permit</button></div>`;
}

export function mountDecidePage(root: HTMLElement, link: { permitId: string; code: string; company: string }): void {
  document.title = `${link.permitId} · SafeTag`;
  let view: DecisionView | null = null;
  let reason = "";
  let busy = false;
  let error = "";
  const args = { permitId: link.permitId, k: link.code, co: link.company };

  function draw(): void {
    root.innerHTML = `<div class="ap">${view ? decideHtml(view, reason, busy, error) : error ? `${bar(link.permitId)}<div class="ap-body"><div class="ap-err">${esc(error)}</div><div class="ap-acts"><button type="button" class="ap-yes" data-load-retry>Try Again</button></div></div>` : `${bar(link.permitId)}<div class="ap-body"><p>Opening the permit…</p></div>`}</div>`;
  }

  // A load error offers Try Again, never words alone (NAV_AUDIT.md item 12). Opened from an email.
  root.addEventListener("click", (e) => {
    if (!view && (e.target as HTMLElement).closest("[data-load-retry]")) {
      error = "";
      draw();
      void load();
    }
  });

  async function load(): Promise<void> {
    try {
      view = await callServer<DecisionView>("loadEewpDecision", args);
      error = "";
    } catch (e) {
      error = messageOf(e);
    }
    draw();
  }

  async function decide(decision: "approved" | "denied"): Promise<void> {
    if (!view || busy) return;
    if (decision === "denied" && !reason.trim()) {
      error = "Give one line for the Deny. The QEW sees it.";
      draw();
      root.querySelector<HTMLTextAreaElement>("#ap-reason")?.focus();
      return;
    }
    busy = true;
    error = "";
    draw();
    try {
      const done = await callServer<DecisionView>("decideEewp", { ...args, decision, reason });
      view = { ...done, photo: view.photo };
    } catch (e) {
      error = `Not recorded. ${messageOf(e)}`;
    }
    busy = false;
    draw();
    window.scrollTo(0, 0);
  }

  root.addEventListener("click", (ev) => {
    const b = (ev.target as HTMLElement).closest<HTMLElement>("[data-ap]");
    if (!b || (b as HTMLButtonElement).disabled) return;
    if (b.dataset.ap === "approve") void decide("approved");
    else if (b.dataset.ap === "deny") void decide("denied");
    else if (b.dataset.ap === "print" && view) {
      const w = window.open("", "_blank");
      if (!w) return;
      w.document.open();
      w.document.write(printHtmlOf(view.permit));
      w.document.close();
    }
  });
  root.addEventListener("input", (ev) => {
    const el = ev.target as HTMLTextAreaElement;
    if (el.id === "ap-reason") reason = el.value;
  });

  draw();
  void load();
}
